import { motion } from "framer-motion";

// Compact animated dashboard used as the hero visual — no external libs.
export default function AnimatedDashboard() {
  const bars = [40, 62, 48, 78, 55, 88, 70, 92, 60];
  const cats = ["Rev", "GM", "CF", "AR", "AP", "WC", "OpEx", "EBITDA", "NP"];

  return (
    <div
      className="relative w-full aspect-[4/5] md:aspect-[4/4.4] rounded-sm vs-glass-strong overflow-hidden"
      data-testid="hero-dashboard"
    >
      {/* Header row */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#d4af37]" />
          <span className="text-[10px] tracking-[0.3em] text-white/70 font-body uppercase">
            Executive View · Q4
          </span>
        </div>
        <span className="font-mono-accent text-[10px] text-white/50">
          LIVE · UAE / IN
        </span>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-3 border-b border-white/10">
        {[
          { k: "Gross Margin", v: "42.8%", d: "+3.1 pts" },
          { k: "EBITDA", v: "18.2%", d: "+1.4 pts" },
          { k: "Cash Days", v: "27d", d: "-6d" },
        ].map((m, i) => (
          <motion.div
            key={m.k}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.15, duration: 0.6 }}
            className={`px-5 py-4 ${i < 2 ? "border-r border-white/10" : ""}`}
          >
            <div className="text-[10px] tracking-widest text-white/50 uppercase font-body">
              {m.k}
            </div>
            <div className="mt-1 font-serif-display text-2xl md:text-3xl text-white">
              {m.v}
            </div>
            <div className="text-[10px] font-mono-accent text-[#d4af37] mt-0.5">
              {m.d}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Line chart */}
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] tracking-[0.25em] text-white/60 uppercase">
            Cash Flow · 12M
          </span>
          <span className="font-mono-accent text-[10px] text-white/40">USD</span>
        </div>
        <svg viewBox="0 0 400 110" className="w-full h-[90px]" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lg" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#d4af37" stopOpacity="0.35" />
              <stop offset="1" stopColor="#d4af37" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* grid */}
          {[20, 45, 70, 95].map((y) => (
            <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />
          ))}
          {/* area */}
          <path
            d="M0,80 L40,68 L80,72 L120,55 L160,60 L200,42 L240,48 L280,34 L320,40 L360,22 L400,28 L400,110 L0,110 Z"
            fill="url(#lg)"
          />
          {/* line */}
          <path
            className="vs-line-anim"
            d="M0,80 L40,68 L80,72 L120,55 L160,60 L200,42 L240,48 L280,34 L320,40 L360,22 L400,28"
            stroke="#d4af37"
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      </div>

      {/* Bar chart */}
      <div className="px-5 py-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] tracking-[0.25em] text-white/60 uppercase">
            Profit Drivers
          </span>
          <span className="font-mono-accent text-[10px] text-white/40">by segment</span>
        </div>
        <div className="flex items-end justify-between h-24 gap-2">
          {bars.map((b, i) => (
            <motion.div
              key={i}
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ delay: 0.7 + i * 0.06, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: "bottom" }}
              className="flex-1 flex flex-col items-center gap-1"
            >
              <div
                className="w-full rounded-t-sm"
                style={{
                  height: `${b}%`,
                  background:
                    i === 5 || i === 7
                      ? "linear-gradient(180deg,#d4af37,rgba(212,175,55,0.4))"
                      : "rgba(255,255,255,0.14)",
                }}
              />
              <span className="text-[8px] tracking-widest text-white/40 font-mono-accent">
                {cats[i]}
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Footer band */}
      <div className="absolute bottom-0 inset-x-0 px-5 py-3 border-t border-white/10 flex items-center justify-between bg-black/30">
        <span className="text-[10px] font-mono-accent text-white/60">
          Insight · Where profit leaks in Q4
        </span>
        <span className="text-[10px] font-mono-accent text-[#d4af37]">
          Recommended Actions · 3
        </span>
      </div>
    </div>
  );
}
